import { Component, type ReactNode, type ErrorInfo } from 'react'
import * as Sentry from '@sentry/react'

interface Props { children: ReactNode }
interface State { hasError: boolean; message: string }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, message: '' }

  static getDerivedStateFromError(err: Error): State {
    return { hasError: true, message: err.message }
  }

  override componentDidCatch(err: Error, info: ErrorInfo) {
    Sentry.captureException(err, { extra: { componentStack: info.componentStack } })
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center justify-center h-full gap-3 text-bb-subtle p-4"
        >
          <span aria-hidden="true" className="text-bb-red text-2xl">⚠</span>
          <p className="text-sm font-semibold text-bb-text">Something went wrong</p>
          {this.state.message && (
            <p className="text-xs text-bb-subtle text-center max-w-xs break-words">{this.state.message}</p>
          )}
          <button
            className="bb-btn-ghost"
            onClick={() => this.setState({ hasError: false, message: '' })}
            aria-label="Retry loading this panel"
          >
            Retry
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
